/**
 * GymMapView Component
 *
 * A read-only viewer for displaying gym floor plans with wall section polygon overlays.
 * Used by climbers to visualize the gym layout and navigate to specific wall sections.
 *
 * Features:
 * - Floor plan image as background
 * - Wall section polygons with labels
 * - Pan and zoom (mouse drag, wheel, touch pinch)
 * - Hover states on polygons
 * - Click/tap to select wall sections
 * - View transition animations (fade-in/scale on mount)
 *
 * @example
 * ```tsx
 * import { GymMapView } from '@/components/gym-map/GymMapView';
 *
 * const FloorPlan = ({ gym }) => (
 *   <GymMapView
 *     floorPlan={gym.floorPlan}
 *     containerWidth={800}
 *     containerHeight={600}
 *     onWallSectionClick={(name) => navigate(`?wall=${name}`)}
 *     animate={true}
 *   />
 * );
 * ```
 */

import { useMemo, useState, useEffect, type CSSProperties } from 'react';
import { Image as KonvaImage } from 'react-konva';
import { MapStage } from './MapStage';
import { WallPolygon } from './WallPolygon';
import { useFloorPlanImage } from '@/hooks/useFloorPlanImage';
import { denormalizePolygon, flattenPolygonPoints } from '@/utils/coordinates';
import type { GymFloorPlan } from '@flash/shared';

/**
 * Props for GymMapView component
 */
export interface GymMapViewProps {
  /** Floor plan configuration with image and regions */
  floorPlan: GymFloorPlan;

  /** Width of the container in pixels */
  containerWidth: number;

  /** Height of the container in pixels */
  containerHeight: number;

  /** Currently selected wall section name (optional) */
  selectedWallSection?: string;

  /** Called when a wall section polygon is clicked/tapped */
  onWallSectionClick?: (wallSectionName: string) => void;

  /** Additional CSS class name for the container */
  className?: string;

  /** Whether to enable highlight flash animation on polygon click/tap (default: false) */
  enableHighlightOnClick?: boolean;

  /** Duration of highlight animation in milliseconds (default: 200) */
  highlightDuration?: number;

  /** Whether to animate the view on mount (default: false) */
  animate?: boolean;

  /** Duration of view transition animation in milliseconds (default: 300) */
  animationDuration?: number;
}

/**
 * Calculate the scale to fit the image within the container while maintaining aspect ratio
 */
const calculateFitScale = (
  imageWidth: number,
  imageHeight: number,
  containerWidth: number,
  containerHeight: number
): number => {
  const scaleX = containerWidth / imageWidth;
  const scaleY = containerHeight / imageHeight;
  return Math.min(scaleX, scaleY);
};

/**
 * GymMapView Component
 *
 * Displays the gym floor plan with interactive wall section polygons.
 * Supports pan and zoom on both desktop and mobile.
 */
export const GymMapView = ({
  floorPlan,
  containerWidth,
  containerHeight,
  selectedWallSection,
  onWallSectionClick,
  className,
  enableHighlightOnClick = false,
  highlightDuration,
  animate = false,
  animationDuration = 300,
}: GymMapViewProps) => {
  // Animation state for view transitions
  const [isVisible, setIsVisible] = useState(!animate);

  // Trigger animation on mount
  useEffect(() => {
    if (animate) {
      // Use requestAnimationFrame to ensure the initial state is rendered first
      const raf = requestAnimationFrame(() => {
        setIsVisible(true);
      });
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
  }, [animate]);

  // Load the floor plan image
  const { image, isLoading, hasError, naturalWidth, naturalHeight } = useFloorPlanImage(
    floorPlan.imageUrl
  );

  // Use stored dimensions if available, otherwise use loaded dimensions
  const imageWidth = floorPlan.imageWidth || naturalWidth;
  const imageHeight = floorPlan.imageHeight || naturalHeight;

  // Calculate initial scale to fit image in container
  const initialScale = useMemo(() => {
    if (imageWidth === 0 || imageHeight === 0) return 1;
    return calculateFitScale(imageWidth, imageHeight, containerWidth, containerHeight);
  }, [imageWidth, imageHeight, containerWidth, containerHeight]);

  // Calculate initial position to center the image
  const initialPosition = useMemo(() => {
    if (imageWidth === 0 || imageHeight === 0) return { x: 0, y: 0 };

    const scaledWidth = imageWidth * initialScale;
    const scaledHeight = imageHeight * initialScale;

    return {
      x: (containerWidth - scaledWidth) / 2,
      y: (containerHeight - scaledHeight) / 2,
    };
  }, [imageWidth, imageHeight, containerWidth, containerHeight, initialScale]);

  // Convert normalized polygon coordinates to pixel coordinates
  const denormalizedRegions = useMemo(() => {
    if (imageWidth === 0 || imageHeight === 0) return [];

    return floorPlan.regions.map((region) => ({
      ...region,
      pixelPoints: flattenPolygonPoints(
        denormalizePolygon(region.polygon, { width: imageWidth, height: imageHeight })
      ),
    }));
  }, [floorPlan.regions, imageWidth, imageHeight]);

  // Handle wall section click
  const handleWallSectionClick = (wallSectionName: string) => {
    onWallSectionClick?.(wallSectionName);
  };

  // Compute animation styles
  const animationStyle: CSSProperties = animate
    ? {
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.95)',
        transition: `opacity ${animationDuration}ms ease-out, transform ${animationDuration}ms ease-out`,
      }
    : {};

  // Loading state
  if (isLoading) {
    return (
      <div
        style={{
          ...containerStyle,
          ...animationStyle,
          width: containerWidth,
          height: containerHeight,
        }}
        className={className}
      >
        <div style={loadingStyle}>
          <div style={spinnerStyle} />
          <p style={loadingTextStyle}>Loading floor plan...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div
        style={{
          ...containerStyle,
          ...animationStyle,
          width: containerWidth,
          height: containerHeight,
        }}
        className={className}
      >
        <div style={errorStyle}>
          <p style={errorTextStyle}>Failed to load floor plan image</p>
        </div>
      </div>
    );
  }

  // Build props conditionally to avoid passing undefined with exactOptionalPropertyTypes
  const mapStageProps = {
    width: containerWidth,
    height: containerHeight,
    imageWidth,
    imageHeight,
    initialViewport: {
      x: initialPosition.x,
      y: initialPosition.y,
      scale: initialScale,
    },
    minScale: initialScale * 0.5,
    maxScale: 4,
    ...(className ? { className } : {}),
  };

  return (
    <div
      style={{ ...wrapperStyle, ...animationStyle, width: containerWidth, height: containerHeight }}
    >
      <MapStage {...mapStageProps}>
        {/* Floor plan background image */}
        {image && (
          <KonvaImage image={image} width={imageWidth} height={imageHeight} listening={false} />
        )}

        {/* Wall section polygons */}
        {denormalizedRegions.map((region) => {
          // Build props conditionally to avoid undefined with exactOptionalPropertyTypes
          const polygonProps = {
            id: `wall-${region.wallSectionName}`,
            points: region.pixelPoints,
            label: region.wallSectionName,
            isSelected: selectedWallSection === region.wallSectionName,
            onClick: () => handleWallSectionClick(region.wallSectionName),
            onTap: () => handleWallSectionClick(region.wallSectionName),
            enableHighlightAnimation: enableHighlightOnClick,
            ...(highlightDuration !== undefined ? { highlightDuration } : {}),
          };
          return <WallPolygon key={region.wallSectionName} {...polygonProps} />;
        })}
      </MapStage>
    </div>
  );
};

// Styles
const wrapperStyle: CSSProperties = {
  borderRadius: '8px',
  overflow: 'hidden',
};

const containerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  borderRadius: '8px',
  overflow: 'hidden',
};

const loadingStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
};

const spinnerStyle: CSSProperties = {
  width: '32px',
  height: '32px',
  border: '3px solid var(--color-border, #e5e7eb)',
  borderTopColor: 'var(--color-primary, #3b82f6)',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const errorStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-error, #ef4444)',
};

export default GymMapView;
